import { z } from "zod";
import {
  MAX_IMPORT_COLUMNS,
  MAX_PRICE_CENTS,
  MAX_STOCK_QTY_Q4,
  MAX_TITLE_CENTS,
  cellNum,
  cellText,
  formatBRL,
  formatDateBR,
  formatPct,
  formatQty,
  normalizeHeader,
  onlyDigits,
  parseDateCell,
  parseDecimalCell,
  type CellResult,
  type SheetCell,
} from "@cx/domain";
import { ulidParam } from "./ids";

// ===== Importação por planilha (T1.15, RF-PRF-09 parcial) =====

/** Tipos importáveis; cada um exige o `create` (e, no modo upsert, o `update`) do módulo de mesmo nome. */
export const IMPORT_ENTITIES = ["products", "contacts", "receivables", "payables"] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];
/** `create` = só incluir; `upsert` = incluir e atualizar (produto pelo SKU, contato pelo CPF/CNPJ ou código). */
export const IMPORT_MODES = ["create", "upsert"] as const;
export type ImportMode = (typeof IMPORT_MODES)[number];
export const IMPORT_ENTITY_LABEL: Record<ImportEntity, string> = {
  products: "Produtos",
  contacts: "Contatos",
  receivables: "Contas a receber",
  payables: "Contas a pagar",
};
/** Contas não têm atualização por planilha (não há chave natural confiável). */
export const importModeAllowed = (entity: ImportEntity, mode: ImportMode) =>
  mode === "create" || entity === "products" || entity === "contacts";

/** Tetos por empresa (decisão 9 do plano). */
export const MAX_IMPORTS_IN_PROGRESS = 3;
export const MAX_IMPORTS_PER_DAY = 20;
export const MAX_IMPORT_ROWS_PER_DAY = 50_000;
/** Prévia validada pode ser confirmada até 24 h depois do envio. */
export const IMPORT_TTL_MS = 24 * 60 * 60 * 1000;
/** Linhas por chamada de validação (stage) e por transação de gravação. */
export const IMPORT_STAGE_BATCH = 200;
export const IMPORT_COMMIT_BATCH = 100;

export type ImportStatus = "validating" | "validated" | "committing" | "done" | "failed" | "canceled";
export type ImportRowStatus = "error" | "valid" | "done" | "failed";
export type ImportCellError = { column: string; message: string };

// ---------- colunas ----------

type Kind =
  | { t: "text"; max: number }
  | { t: "money" }
  | { t: "pct" }
  | { t: "qty" }
  | { t: "kg" }
  | { t: "date" }
  | { t: "digit"; allowed: readonly number[] }
  | { t: "enum"; map: Readonly<Record<string, string>>; labels: Readonly<Record<string, string>> }
  | { t: "list" }
  | { t: "document" }
  | { t: "cep" };

export type ImportColumn = {
  /** Caminho no objeto de valores (ex.: `tax.ncm`, `address.cep`). */
  path: string;
  /** Nome no modelo, na prévia e nos relatórios. */
  label: string;
  /** Outros nomes aceitos no cabeçalho (já normalizados por `normalizeHeader`). */
  aliases: readonly string[];
  kind: Kind;
  /** Exige products:read_cost. */
  cost?: boolean;
};

const text = (max: number): Kind => ({ t: "text", max });
const col = (
  path: string,
  label: string,
  kind: Kind,
  aliases: string[] = [],
  extra: Partial<ImportColumn> = {},
) => ({ path, label, kind, aliases: [normalizeHeader(label), ...aliases], ...extra }) satisfies ImportColumn;

const STATUS = { ativo: "active", inativo: "inactive" } as const;
const STATUS_LABEL = { active: "Ativo", inactive: "Inativo" };

const PRODUCT_COLUMNS: readonly ImportColumn[] = [
  col("sku", "SKU", text(60), ["codigo", "codigo sku", "cod", "codigo do produto"]),
  col("name", "Nome", text(120), ["descricao", "nome do produto", "produto"]),
  col(
    "kind",
    "Tipo",
    {
      t: "enum",
      map: { produto: "P", p: "P", servico: "S", s: "S" },
      labels: { P: "Produto", S: "Serviço" },
    },
    ["tipo do produto"],
  ),
  col("status", "Situação", { t: "enum", map: STATUS, labels: STATUS_LABEL }, ["status"]),
  col("unit", "Unidade", text(6), ["un", "und", "unid"]),
  col("priceCents", "Preço de venda", { t: "money" }, [
    "preco",
    "preco venda",
    "valor de venda",
    "valor venda",
    "preco r",
    "preco de venda r",
  ]),
  col(
    "costCents",
    "Preço de custo",
    { t: "money" },
    ["custo", "preco custo", "custo r", "preco de custo r"],
    {
      cost: true,
    },
  ),
  col("gtin", "GTIN", text(14), ["ean", "gtin ean", "codigo de barras"]),
  col("gtinTax", "GTIN tributário", text(14), ["ean tributario", "gtin ean tributario"]),
  col("tax.ncm", "NCM", text(10), ["classificacao fiscal"]),
  col("tax.cest", "CEST", text(9)),
  col("tax.origin", "Origem", { t: "digit", allowed: [0, 1, 2, 3, 4, 5, 6, 7, 8] }, ["origem da mercadoria"]),
  col("shortDescription", "Descrição curta", text(255)),
  col("netWeightG", "Peso líquido (kg)", { t: "kg" }, ["peso liquido"]),
  col("grossWeightG", "Peso bruto (kg)", { t: "kg" }, ["peso bruto"]),
  col("stockMinQ4", "Estoque mínimo", { t: "qty" }),
  col("stockMaxQ4", "Estoque máximo", { t: "qty" }),
  col("location", "Localização", text(60), ["localizacao no estoque"]),
  col("notes", "Observações", text(5000), ["observacao", "obs"]),
];

const CONTACT_COLUMNS: readonly ImportColumn[] = [
  col("code", "Código", text(30), ["cod", "codigo do contato"]),
  col("name", "Nome", text(120), ["razao social", "nome razao social", "nome completo"]),
  col("tradeName", "Fantasia", text(120), ["nome fantasia"]),
  col(
    "personType",
    "Tipo pessoa",
    {
      t: "enum",
      map: {
        fisica: "F",
        f: "F",
        pf: "F",
        "pessoa fisica": "F",
        juridica: "J",
        j: "J",
        pj: "J",
        "pessoa juridica": "J",
        estrangeiro: "E",
        e: "E",
      },
      labels: { F: "Física", J: "Jurídica", E: "Estrangeiro" },
    },
    ["tipo de pessoa", "pessoa"],
  ),
  col("document", "CPF/CNPJ", { t: "document" }, ["cnpj cpf", "cpf", "cnpj", "documento"]),
  col("ie", "IE", text(20), ["inscricao estadual"]),
  col("ieIndicator", "Contribuinte", { t: "digit", allowed: [1, 2, 9] }, ["indicador ie", "indicador de ie"]),
  col("email", "E-mail", text(254), ["email"]),
  col("nfeEmail", "E-mail NF-e", text(254), ["email nfe", "e mail nfe", "e mail para envio da nf e"]),
  col("phone", "Telefone", text(30), ["fone"]),
  col("mobile", "Celular", text(30)),
  col("address.cep", "CEP", { t: "cep" }),
  col("address.street", "Endereço", text(120), ["logradouro", "rua"]),
  col("address.number", "Número", text(20), ["numero do endereco", "no"]),
  col("address.complement", "Complemento", text(80)),
  col("address.district", "Bairro", text(80)),
  col("address.city", "Cidade", text(80), ["municipio"]),
  col("address.uf", "UF", text(2), ["estado"]),
  col(
    "status",
    "Situação",
    {
      t: "enum",
      map: { ...STATUS, "sem movimento": "no_movement", "sem movimentacao": "no_movement" },
      labels: { ...STATUS_LABEL, no_movement: "Sem movimento" },
    },
    ["status"],
  ),
  col("typeNames", "Tipos", { t: "list" }, ["tipo de contato", "tipos de contato"]),
  col("customerSince", "Cliente desde", { t: "date" }),
  col("notes", "Observações", text(5000), ["observacao", "obs"]),
];

const titleColumns = (side: "R" | "P"): readonly ImportColumn[] => [
  col("contactName", side === "R" ? "Cliente" : "Fornecedor", text(120), [
    "contato",
    "nome do contato",
    side === "R" ? "nome do cliente" : "nome do fornecedor",
  ]),
  col("contactDocument", "CPF/CNPJ", { t: "document" }, ["cnpj cpf", "cpf", "cnpj"]),
  col("amountCents", "Valor", { t: "money" }, ["valor r", "valor original", "valor da conta"]),
  col("issueDate", "Emissão", { t: "date" }, ["data de emissao", "data emissao"]),
  col("competenceDate", "Competência", { t: "date" }, ["data de competencia"]),
  col("dueDate", "Vencimento", { t: "date" }, ["data de vencimento", "data vencimento"]),
  col("description", "Histórico", text(2000), ["descricao"]),
  col("documentNumber", "Nº documento", text(30), ["n documento", "numero do documento", "numero documento"]),
  col("categoryName", "Categoria", text(120), ["categoria financeira"]),
  col("paymentMethodName", "Forma de pagamento", text(60), ["forma pagamento"]),
  col("interestMonthPct4", "Juros mensal (%)", { t: "pct" }, ["juros"]),
  col("finePct4", "Multa (%)", { t: "pct" }),
];

export const IMPORT_COLUMNS: Record<ImportEntity, readonly ImportColumn[]> = {
  products: PRODUCT_COLUMNS,
  contacts: CONTACT_COLUMNS,
  receivables: titleColumns("R"),
  payables: titleColumns("P"),
};

/** Cabeçalho do modelo para baixar (sem a coluna de custo para quem não tem products:read_cost). */
export const importTemplateHeaders = (entity: ImportEntity, o: { withCost: boolean }) =>
  IMPORT_COLUMNS[entity].filter((c) => o.withCost || !c.cost).map((c) => c.label);

// ---------- plano de colunas (cabeçalho) ----------

export type ImportPlan = {
  /** Índice da coluna na planilha → caminho. */
  columns: { index: number; path: string }[];
  /** Nomes reconhecidos (rótulo do modelo), na ordem da planilha. */
  labels: string[];
  /** Cabeçalhos desconhecidos (ignorados, aviso na prévia). */
  ignored: string[];
  /** Erros do arquivo: a importação não segue. */
  errors: string[];
};

const has = (p: ImportPlan, path: string) => p.columns.some((c) => c.path === path);

/** Liga o cabeçalho às colunas do tipo e confere colunas repetidas, obrigatórias e o custo sem permissão. */
export function planImportColumns(
  entity: ImportEntity,
  mode: ImportMode,
  header: readonly (SheetCell | undefined)[],
  o: { withCost: boolean },
): ImportPlan {
  const defs = IMPORT_COLUMNS[entity];
  const plan: ImportPlan = { columns: [], labels: [], ignored: [], errors: [] };
  if (header.length > MAX_IMPORT_COLUMNS)
    plan.errors.push(`A planilha tem mais de ${MAX_IMPORT_COLUMNS} colunas.`);
  header.forEach((raw, index) => {
    const name = cellText(raw);
    if (!name) return;
    const key = normalizeHeader(name);
    const def = defs.find((d) => d.aliases.includes(key));
    if (!def) return void plan.ignored.push(name);
    if (has(plan, def.path)) return void plan.errors.push(`A coluna "${def.label}" aparece mais de uma vez.`);
    if (def.cost && !o.withCost)
      return void plan.errors.push(
        `Você não tem permissão para importar o "${def.label}". Tire a coluna da planilha ou peça a permissão.`,
      );
    plan.columns.push({ index, path: def.path });
    plan.labels.push(def.label);
  });
  if (plan.columns.length === 0)
    plan.errors.push(
      "Nenhuma coluna reconhecida. Use a primeira linha para os nomes das colunas (veja o modelo).",
    );
  const need = (ok: boolean, msg: string) => void (!ok && plan.columns.length > 0 && plan.errors.push(msg));
  if (entity === "products")
    need(
      mode === "create" ? has(plan, "name") : has(plan, "sku"),
      mode === "create" ? 'Falta a coluna "Nome".' : 'Para atualizar, a planilha precisa da coluna "SKU".',
    );
  if (entity === "contacts")
    need(
      mode === "create" ? has(plan, "name") : has(plan, "document") || has(plan, "code"),
      mode === "create"
        ? 'Falta a coluna "Nome".'
        : 'Para atualizar, a planilha precisa da coluna "CPF/CNPJ" ou "Código".',
    );
  if (entity === "receivables" || entity === "payables") {
    need(has(plan, "amountCents"), 'Falta a coluna "Valor".');
    need(has(plan, "dueDate"), 'Falta a coluna "Vencimento".');
    need(
      has(plan, "contactName") || has(plan, "contactDocument"),
      `Falta a coluna "${IMPORT_COLUMNS[entity][0]!.label}" ou "CPF/CNPJ".`,
    );
  }
  return plan;
}

// ---------- linha ----------

export type MappedImportRow = {
  values: Record<string, unknown>;
  errors: ImportCellError[];
  /** Identificação da linha no relatório (SKU, nome, documento…). */
  key: string | null;
};

const ok = <T>(value: T): CellResult<T> => ({ ok: true, value });
const bad = (error: string): CellResult<never> => ({ ok: false, error });

/** Documento: só dígitos/letras; número do XLSX perde zeros à esquerda (CPF volta a 11 dígitos, CNPJ a 14). */
function documentCell(c: SheetCell | undefined): CellResult<string | null> {
  const t = cellText(c);
  if (!t) return ok(null);
  if (cellNum(c) === null) return ok(t);
  const d = onlyDigits(t);
  return ok(d.length <= 11 ? d.padStart(11, "0") : d.padStart(14, "0"));
}

function parseCell(kind: Kind, c: SheetCell | undefined, o: { date1904: boolean }): CellResult<unknown> {
  switch (kind.t) {
    case "text": {
      const t = cellText(c);
      if (t.length > kind.max) return bad(`Até ${kind.max} caracteres`);
      return ok(t === "" ? null : t);
    }
    case "money":
      return parseDecimalCell(c, 2, { max: Math.max(MAX_PRICE_CENTS, MAX_TITLE_CENTS) });
    case "pct": {
      // Percentual vazio fica de fora (o contrato tem padrão 0), como enum e dígito.
      const r = parseDecimalCell(c, 4, { max: 1_000_000 });
      return r.ok && r.value === null ? ok(undefined) : r;
    }
    case "qty":
      return parseDecimalCell(c, 4, { max: MAX_STOCK_QTY_Q4 });
    case "kg":
      return parseDecimalCell(c, 3, { max: 100_000_000 });
    case "date":
      return parseDateCell(c, o);
    case "digit": {
      const t = cellText(c);
      if (t === "") return ok(undefined);
      const n = Number(/^(\d)(?:\D|$)/.exec(t)?.[1] ?? "x");
      return kind.allowed.includes(n) ? ok(n) : bad(`Use ${kind.allowed.join(", ")}`);
    }
    case "enum": {
      const t = cellText(c);
      if (t === "") return ok(undefined);
      const v = kind.map[normalizeHeader(t)];
      return v ? ok(v) : bad(`Use ${Object.values(kind.labels).join(", ")}`);
    }
    case "list": {
      const items = cellText(c)
        .split(/[,;|]/)
        .map((s) => s.trim())
        .filter(Boolean);
      return items.length > 10 ? bad("No máximo 10 itens") : ok(items);
    }
    case "document":
      return documentCell(c);
    case "cep": {
      const t = cellText(c);
      if (t === "") return ok(null);
      return ok(cellNum(c) === null ? t : onlyDigits(t).padStart(8, "0"));
    }
  }
}

function setPath(target: Record<string, unknown>, path: string, value: unknown) {
  const parts = path.split(".");
  let o = target;
  for (const p of parts.slice(0, -1)) o = (o[p] ??= {}) as Record<string, unknown>;
  o[parts.at(-1)!] = value;
}

export function getPath(source: Record<string, unknown>, path: string): unknown {
  let o: unknown = source;
  for (const p of path.split(".")) {
    if (typeof o !== "object" || o === null || !Object.hasOwn(o, p)) return undefined;
    o = (o as Record<string, unknown>)[p];
  }
  return o;
}

const KEY_PATHS: Record<ImportEntity, readonly string[]> = {
  products: ["sku", "name"],
  contacts: ["document", "code", "name"],
  receivables: ["contactDocument", "contactName", "documentNumber"],
  payables: ["contactDocument", "contactName", "documentNumber"],
};

/**
 * Converte as células de uma linha nos valores normalizados (centavos, q4, pct4, gramas, datas ISO) pelo plano.
 * Coluna presente e vazia = null (limpa o campo na atualização), exceto as que têm padrão no contrato (situação, tipo,
 * origem, contribuinte, percentuais), que ficam de fora. Erros por coluna; a validação completa (contrato
 * Zod, cadastro existente, regras do serviço) é no DO.
 */
export function mapImportRow(
  entity: ImportEntity,
  plan: Pick<ImportPlan, "columns">,
  cells: readonly (SheetCell | undefined)[],
  o: { date1904?: boolean } = {},
): MappedImportRow {
  const defs = IMPORT_COLUMNS[entity];
  const values: Record<string, unknown> = {};
  const errors: ImportCellError[] = [];
  for (const { index, path } of plan.columns) {
    const def = defs.find((d) => d.path === path)!;
    const r = parseCell(def.kind, cells[index], { date1904: o.date1904 ?? false });
    // undefined = célula vazia em coluna com padrão no contrato (situação, tipo, origem, percentuais): fica de fora.
    if (r.ok) {
      if (r.value !== undefined) setPath(values, path, r.value);
    } else errors.push({ column: def.label, message: r.error });
  }
  if (entity === "contacts") {
    const doc = values.document;
    if (values.personType == null && typeof doc === "string") {
      const d = onlyDigits(doc);
      if (d.length === 11) values.personType = "F";
      else if (d.length === 14) values.personType = "J";
    }
  }
  const keyPath = KEY_PATHS[entity].find((p) => typeof getPath(values, p) === "string");
  return { values, errors, key: keyPath ? (getPath(values, keyPath) as string) : null };
}

/** Coluna da planilha para um caminho de erro do contrato (Zod) ou do serviço. */
const ISSUE_ALIASES: Record<string, string> = {
  addresses: "address",
  contactId: "contactName",
  financialCategoryId: "categoryName",
  paymentMethodId: "paymentMethodName",
  typeIds: "typeNames",
};
export function importColumnLabel(entity: ImportEntity, path: readonly PropertyKey[]): string {
  const parts = path.filter((p): p is string => typeof p === "string").map((p) => ISSUE_ALIASES[p] ?? p);
  const defs = IMPORT_COLUMNS[entity];
  for (let n = parts.length; n > 0; n--) {
    const def = defs.find((d) => d.path === parts.slice(0, n).join("."));
    if (def) return def.label;
  }
  return "Linha";
}

/** Valores normalizados como texto pt-BR, por coluna (prévia: mostra como a planilha foi entendida). */
export function importValuesDisplay(
  entity: ImportEntity,
  values: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const def of IMPORT_COLUMNS[entity]) {
    const v = getPath(values, def.path);
    if (v === undefined) continue;
    out[def.label] = v === null ? "" : displayValue(def.kind, v);
  }
  return out;
}

function displayValue(kind: Kind, v: unknown): string {
  switch (kind.t) {
    case "money":
      return formatBRL(v as number);
    case "pct":
      return formatPct(v as number);
    case "qty":
      return formatQty(v as number);
    case "kg":
      return `${formatQty((v as number) * 10)} kg`;
    case "date":
      return formatDateBR(v as string);
    case "enum":
      return kind.labels[v as string] ?? String(v);
    case "list":
      return (v as string[]).join(", ");
    default:
      return String(v);
  }
}

// ---------- API ----------

export const importEntityParam = z.enum(IMPORT_ENTITIES, "Tipo de importação inválido");
export const importModeQuery = z.object({ mode: z.enum(IMPORT_MODES, "Modo inválido").default("create") });
export const importParams = z.object({ entity: importEntityParam, id: ulidParam });
export const importRowsQuery = z.object({
  status: z.enum(["error", "valid", "done", "failed", "all"]).default("all"),
  cursor: z.coerce.number().int().min(0).max(1_000_000).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ImportRowsQuery = z.output<typeof importRowsQuery>;
export const IMPORT_REPORT_KINDS = ["errors", "result"] as const;
export type ImportReportKind = (typeof IMPORT_REPORT_KINDS)[number];
export const importReportQuery = z.object({ kind: z.enum(IMPORT_REPORT_KINDS, "Relatório inválido") });
export const importListQuery = z.object({ limit: z.coerce.number().int().min(1).max(50).default(20) });

export type ImportCounts = {
  total: number;
  valid: number;
  errors: number;
  created: number;
  updated: number;
  failed: number;
};
export type ImportView = {
  id: string;
  entity: ImportEntity;
  mode: ImportMode;
  status: ImportStatus;
  fileName: string;
  fileFormat: "csv" | "xlsx";
  sizeBytes: number;
  createdAt: string;
  validatedAt: string | null;
  committedAt: string | null;
  finishedAt: string | null;
  /** Até quando a prévia pode ser confirmada. */
  expiresAt: string;
  counts: ImportCounts;
  /** Colunas reconhecidas (rótulo do modelo) e cabeçalhos ignorados. */
  columns: string[];
  ignoredColumns: string[];
  /** Erros do arquivo (não segue) e avisos (planilha igual já importada etc.). */
  fileErrors: string[];
  warnings: string[];
  jobId: string | null;
  /** Relatórios prontos; o link (assinado, 60 s, só de quem enviou) sai de `POST .../report`. */
  hasErrorReport: boolean;
  hasResultReport: boolean;
};
export type ImportRowView = {
  rowNo: number;
  status: ImportRowStatus;
  key: string | null;
  errors: ImportCellError[];
  /** Valores entendidos, por coluna (só enquanto a importação não terminou). */
  values: Record<string, string> | null;
  outcome: "created" | "updated" | null;
  entityId: string | null;
  message: string | null;
};
export type ImportRowsResult = { data: ImportRowView[]; nextCursor: number | null };
export type ImportCreated = { importId: string; jobId: string };
